import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { AlertCircle, Folder, Plus, Pencil, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { expenseCategoriesApi } from '../../lib/api';

export default function ExpenseCategoriesSettings() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState({ name: '', description: '', active: true });
    const [saving, setSaving] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const r = await expenseCategoriesApi.list(false);
            setItems(r.data);
        } catch {
            toast.error('Kategorien konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditingId(null);
        setForm({ name: '', description: '', active: true });
        setDialogOpen(true);
    };

    const openEdit = (cat) => {
        setEditingId(cat.id);
        setForm({ name: cat.name, description: cat.description || '', active: cat.active });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (form.name.trim().length < 2) { toast.error('Name benötigt mindestens 2 Zeichen'); return; }
        setSaving(true);
        try {
            if (editingId) {
                await expenseCategoriesApi.update(editingId, form);
                toast.success('Kategorie aktualisiert');
            } else {
                await expenseCategoriesApi.create(form);
                toast.success('Kategorie angelegt');
            }
            setDialogOpen(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSaving(false);
        }
    };

    const handleToggleActive = async (cat) => {
        try {
            await expenseCategoriesApi.update(cat.id, { active: !cat.active });
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Aktualisierung fehlgeschlagen');
        }
    };

    const handleDelete = async (cat) => {
        if (!window.confirm(`Kategorie „${cat.name}" wirklich löschen?`)) return;
        try {
            await expenseCategoriesApi.remove(cat.id);
            toast.success('Kategorie gelöscht');
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
        }
    };

    return (
        <div className="space-y-4" data-testid="expense-categories-settings">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-xl font-bold font-heading">Ausgabenkategorien</h2>
                    <p className="text-sm text-muted-foreground mt-1">Definieren Sie Kategorien für die Seminar-Ausgaben. Deaktivierte Kategorien werden bei neuen Ausgaben nicht mehr angezeigt.</p>
                </div>
                <Button onClick={openCreate} data-testid="cat-create-btn"><Plus className="h-4 w-4 mr-1" /> Neue Kategorie</Button>
            </div>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-base font-heading flex items-center gap-2">
                        <Folder className="h-4 w-4" />
                        Ausgabenkategorien
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="border-b text-xs uppercase tracking-wider text-muted-foreground">
                                        <th className="text-left px-3 py-2 w-12">#</th>
                                        <th className="text-left px-3 py-2">Name</th>
                                        <th className="text-left px-3 py-2">Beschreibung</th>
                                        <th className="text-left px-3 py-2 w-20">Status</th>
                                        <th className="text-right px-3 py-2 w-28">Aktionen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {items.map((cat, idx) => (
                                        <tr key={cat.id} className="border-b last:border-b-0 hover:bg-muted/20" data-testid={`cat-row-${cat.id}`}>
                                            <td className="px-3 py-2.5 text-muted-foreground">{idx + 1}</td>
                                            <td className="px-3 py-2.5 font-medium">
                                                {cat.name}
                                                {cat.is_system && <Badge variant="secondary" className="ml-2 text-[10px] py-0">System</Badge>}
                                            </td>
                                            <td className="px-3 py-2.5 text-muted-foreground">{cat.description || '—'}</td>
                                            <td className="px-3 py-2.5">
                                                <Switch checked={cat.active} onCheckedChange={() => handleToggleActive(cat)} data-testid={`cat-toggle-${cat.id}`} />
                                            </td>
                                            <td className="px-3 py-2.5 text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button variant="ghost" size="sm" onClick={() => openEdit(cat)} data-testid={`cat-edit-${cat.id}`}>
                                                        <Pencil className="h-3.5 w-3.5" />
                                                    </Button>
                                                    {!cat.is_system && (
                                                        <Button variant="ghost" size="sm" onClick={() => handleDelete(cat)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`cat-delete-${cat.id}`}>
                                                            <Trash2 className="h-3.5 w-3.5" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            <Card className="border-amber-300 bg-amber-50/50">
                <CardContent className="pt-4 text-sm text-amber-900 space-y-2">
                    <div className="flex items-center gap-2 font-medium">
                        <AlertCircle className="h-4 w-4" />
                        Hinweis zur Kategorieverwaltung
                    </div>
                    <ul className="space-y-1 ml-6 list-disc text-amber-900/90">
                        <li>Kategorien, die bereits verwendet werden, können nicht gelöscht werden</li>
                        <li>Deaktivierte Kategorien sind bei neuen Ausgaben nicht mehr auswählbar</li>
                        <li>Bestehende Ausgaben behalten ihre ursprüngliche Kategorie</li>
                        <li>Die Kategorie „Sonstiges" kann nicht gelöscht werden</li>
                    </ul>
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-md" data-testid="cat-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingId ? 'Kategorie bearbeiten' : 'Neue Kategorie'}</DialogTitle>
                        <DialogDescription>Gibt der Liste der Ausgabenkategorien einen weiteren Eintrag.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <Label htmlFor="cat-name">Name *</Label>
                            <Input id="cat-name" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} maxLength={80} data-testid="cat-name-input" />
                        </div>
                        <div>
                            <Label htmlFor="cat-desc">Beschreibung</Label>
                            <Textarea id="cat-desc" rows={2} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} maxLength={200} data-testid="cat-desc-input" />
                        </div>
                        <div className="flex items-center justify-between">
                            <Label htmlFor="cat-active">Aktiv</Label>
                            <Switch id="cat-active" checked={form.active} onCheckedChange={(v) => setForm(f => ({ ...f, active: v }))} />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Abbrechen</Button>
                        <Button onClick={handleSave} disabled={saving} data-testid="cat-save-btn">
                            {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
                            {editingId ? 'Speichern' : 'Anlegen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
